<template>
  <div class="modal-invoice-view-wrap">
    <portal to="dash-modal">
      <InvoiceFinanceModal
        v-if="!loading && displayFinanceModal && invoicePDF"
        :invoice="invoice"
        :invoice-pdf="invoicePDF"
        @close="closeFinanceModal"
      />

      <!-- only for mark as sent -->
      <InvoiceSendAdjustModal 
        v-if="!showInvoicesModal && showAdjustModal && invoice"
        :invoice-id="invoice.id"
        :mark-as-sent="markedAsSent"
        @close="closeAdjustModal"
        @marked-as-sent="() => { $emit('invoice-updated'); showAdjustModal = false }"
      />

      <InvoiceDesignModal
        v-if="showDesign"
        @layout-updated="refreshPDF"
        @close="closeDesignModal"
      />
    </portal>

    <!-- MAIN -->
    <div class="mastermodal-view-wrap">
      <div class="mastermodal-view--left">
        <div v-if="!isRecurrent" class="mastermodal-mobile-status">
          <InvoiceStatusBadge :is-quotation="isQuotation" />
        </div>
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' }, className : 'os-theme-dark os-theme-gekko' }">
          <div v-if="detailView === 'details'" class="mastermodal-view-tabs--details">
            
            <div v-if="allowRaboFinancing && !isRecurrent" class="detail-tab--section detail-tab--section__financing">
              <b-alert show class="gk-alert mb-0" variant="info">
                <div @click="manualFinanceToggle()" class="cursor-pointer">
                  <h4 class="mt-0">{{ $gettext('Receive payment now') }}</h4>
                  <p>
                    {{ $gettext('Use Rabobank Invoice financing and receive payment for this invoice right now.') }}
                  </p>
                  <GekkoButton
                    @click="manualFinanceToggle()"
                    :text="$gettext('Request financing')"
                    variant="primary"
                    size="sm"
                  />
                </div>
              </b-alert>
            </div>

            <div v-if="debug" class="detail-tab--section">
              <div class="detail-tab--section__line text-danger">
                <h4>Debug</h4>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div class="">
                  {{ userAgent }}
                </div>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div>Mobile OS</div>
                <div class="">
                  {{ mobileOS }}
                </div>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div>PDF Viewer</div>
                <div class="">
                  {{ (viewerEnabled) ? 'Enabled' : 'Disabled' }}
                </div>
              </div>
            </div>
            
            <div class="detail-tab--section">
              <!-- TOTAL -->
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Total') }}</h4>
                <div class="total-amount" v-html="totalAmount" />
              </div>

              <!-- Number / name -->
              <div v-if="!isRecurrent" class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Quotation number') }}</div>
                <div v-else>{{ $gettext('Invoice number') }}</div>
                <div class="strong">
                  {{ invoice.number }}
                </div>
              </div>

              <!-- Date / period -->
              <div class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Quotation date') }}</div>
                <div v-else>{{ $gettext('Invoice date') }}</div>
                <div class="">
                  {{ invoice.invoice_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>

              <div v-if="invoice.status !== 'p'" class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Valid until') }}</div>
                <div v-else>{{ $gettext('Payment due') }}</div>
                <div class="">
                  {{ invoice.due_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>
              <div class="detail-tab--section__line">
                <div>{{ $gettext('Customer') }}</div>
                <div class="">
                  {{ invoice.recipient_customer.name }}
                </div>
              </div>
              <div v-if="invoice.project" class="detail-tab--section__line">
                <div>{{ $gettext('Project') }}</div>
                <div class="">
                  {{ invoice.project.name }}
                </div>
              </div>
            </div>


            <!-- Schedule summary -->
            <div v-if="scheduleSummary" @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')" class="detail-tab--section cursor-pointer">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Scheduled') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <div class="">
                  <b-alert show class="gk-alert mb-0" variant="info">
                    <font-awesome-icon icon="fa-regular fa-clock-rotate-left" />
                    {{ scheduleSummary }}
                  </b-alert>
                </div>
              </div>
            </div>
            

            <!-- Files -->
            <div v-if="isQuotation && attachedDocuments && attachedDocuments.length > 0" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Files') }}</h4>
              </div>
              <div v-for="file in attachedDocuments" :key="file.id" class="detail-tab--section__line">
                <div>{{ file.title }}</div>
                <div>
                  <GekkoButton 
                    @click="downloadDocument(file)"
                    icon="fa-solid fa-download"
                    variant="link" 
                    class="text-info p-0"
                    size="sm"
                  />
                </div>
              </div>
            </div>

            <!-- Message -->
            <div v-if="invoice.message && invoice.message != ''" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Message') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <div class="">
                  {{ invoice.message }}
                </div>
              </div>
            </div>

           <!-- History -->
            <div v-if="!isRecurrent" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('History') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <TimelineWidget v-if="invoiceId && !isQuotation" :widget="{ config: { object_type: 'invoice', invoice_id: invoiceId } }" />
                <TimelineWidget v-if="invoiceId && isQuotation" :widget="{ config: { object_type: 'quotation', quotation_id: invoiceId } }" />
              </div>
            </div>

          </div>
        </overlay-scrollbars>
      </div>

      <div class="mastermodal-view--right" :class="{ 'expanded': previewExpanded, 'has-badge': !currentCompany.is_premium }">
        <div class="mastermodal-view--right__handle" @click="previewExpanded = !previewExpanded"/>
        <div class="mastermodal-view--right__document">
          <InvoicePDFPreview 
            :is-quotation="isQuotation" 
            :key="previewPDFId" 
            :invoice-id="invoiceId" 
            ref="invoice-pdf-preview" 
            @show-design="showDesign = true"
          />
        </div>
        
        <div v-if="!currentCompany.is_premium" class="mastermodal-view--right__poweredby">
          <div @click="goToSubscriptions()" id="tt-premium-powered">
            <GkLogo :full="false" :fill="'brand'" /> {{ $gettext('powered by getgekko.com') }}
          </div>
          <b-tooltip :custom-class="'tooltip-padded'" target="tt-premium-powered" placement="top" triggers="hover">
            <div class="tooltip-title">
              {{ $gettext('Powered by getgekko.com') }}
            </div>
            <div class="tooltip-text">
              {{ $gettext('To remove the Gekko logo on your invoice, upgrade to Premium.') }}
            </div>
          </b-tooltip>
        </div>

        <div v-if="!isRecurrent" class="mastermodal-view--right__statuswrap">
          <InvoiceStatusBadge :is-quotation="isQuotation" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import invoices from "@/mixins/invoices";
import pdfviewer from "@/mixins/pdfviewer";

import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GkLogo from "@/components/ui/GkLogo.vue";

import InvoiceFinanceModal from "@/components/invoices/ui/InvoiceFinanceModal";
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"
import InvoicePDFPreview from "@/components/invoices/ui/InvoicePDFPreview"
import InvoiceDesignModal from "@/components/invoices/ui/InvoiceDesignModal"
import InvoiceStatusBadge from "@/components/invoices/ui/InvoiceStatusBadge"
import TimelineWidget from "@/components/dashboard/widgets/TimelineWidget";

export default {
  name: "InvoiceView",
  mixins: [invoices, pdfviewer],
  components: {
    GekkoButton,
    LoadingSpinner,
    GkLogo,
    InvoiceFinanceModal,
    InvoiceSendAdjustModal,
    InvoicePDFPreview,
    InvoiceDesignModal,
    InvoiceStatusBadge,
    TimelineWidget
  },
  props: {
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      invoiceId: null,
      loading: true,
      showInvoicesModal: false,
      showFinanceModal: false,
      showDesign: false,
      manualFinanceModalToggle: false,
      showAdjustModal: false,
      modalType: null,
      markedAsSent: false,
      detailView: 'details',
      previewPDFId: null,
      previewExpanded: false,
      
      debug: false,
    }
  },
  computed: {
    ...mapGetters({ 
      currentUserProfile: 'main/getCurrentUserProfile',
      currentCompany: "main/getCurrentCompany",
      currentLayout: "invoicesLayout/getCurrentLayout",
      creditableStatuses: 'invoices/getCreditableStatuses',
      invoice: "invoices/getInvoice",
      recurrentInvoice: "invoices/getRecurrentInvoice",
      invoiceToCopy: 'invoices/getInvoiceToCopy',
      invoicePDF: "invoices/getInvoicePDF",
      currencyByCode: "main/getCurrencyByCode",
      quotationFiles: 'invoices/getQuotationFiles',
      scheduleSummary: 'invoicesRecurrent/getSummary'
    }),
    allowRaboFinancing () {
      let allow_financing = false
      if (this.invoice) {
        const financingConditions = {
          max_age: 10,
          min_amount: 1000,
          max_amount: 100000,
          min_payment_term: 10,
          max_payment_term: 90,
          status: 's',
          currency: 'EUR'
        }

        const today = new Date(), past_date = new Date(today), invoice_date = new Date(this.invoice.invoice_date)
        past_date.setDate(today.getDate() - (financingConditions.max_age + 1)) // +1 include 10th day

        const is_valid_date = invoice_date >= past_date
        const is_valid_status = (this.invoice.status === financingConditions.status)
        const is_valid_amount = (financingConditions.min_amount <= this.invoice.total_amount && this.invoice.total_amount < financingConditions.max_amount)
        const is_valid_currency = (this.invoice.currency === financingConditions.currency)
        const is_valid_term = (this.invoice.payment_term > financingConditions.min_payment_term && this.invoice.payment_term < financingConditions.max_payment_term)

        // checking: amount, status, currency, date and due_date
        if (is_valid_amount && is_valid_status && is_valid_currency && is_valid_date && is_valid_term) {
          allow_financing = true
        } else {
          // console.log('is valid date', is_valid_date)
          // console.log('is valid status', is_valid_status)
          // console.log('is valid amount', is_valid_amount)
          // console.log('is valid currency', is_valid_currency)
          // console.log('is valid term', is_valid_term)
        }
      }
      return allow_financing
    },
    displayFinanceModal () {
      let display_modal = false
      if (this.manualFinanceModalToggle) {
        display_modal = this.showFinanceModal
      } else {
        // automatic modal popup disabled
        // display_modal = !this.showInvoicesModal && this.allowRaboFinancing && (this.currentUserProfile.show_rabo_notification || this.showFinanceModal)
      }
      return display_modal
    },
    isSent () {
      return (this.invoice && this.invoice.status === 's')
    },
    isRecurrent () {
      return (this.invoice && this.invoice.status === 'recurrent')
    },
    isUploaded () {
      return (this.invoice && this.invoice.uploaded)
    },
    attachedDocuments () {
      return this.quotationFiles
    },
    isEUR () {
      return (this.invoice.currency === 'EUR')
    },
    isPaid () {
      return this.invoice.amount_to_be_paid && (this.invoice.amount_to_be_paid == 0) && (this.invoice.total_amount > 0)
    },
    totalAmountEUR () {
      return this.$options.filters.currencyString(this.invoice.total_amount, 'EUR')
    },
    totalAmountCurrency () {
      return this.$options.filters.currencyString(this.invoice.currency_total_amount, this.invoice.currency)
    },
    amountToBePaidEUR () {
      return this.$options.filters.currencyString(this.invoice.amount_to_be_paid, 'EUR')
    },
    totalAmount () {
      let total_amount = 0

      if (this.isEUR) {
        total_amount = this.totalAmountEUR
      } else {
        if (this.invoice.total_amount && this.invoice.total_amount > 0) {
          total_amount = this.totalAmountCurrency + ' <span>(' + this.totalAmountEUR + ')</span>'
        } else {
          total_amount = this.totalAmountCurrency
        }
      }

      return total_amount
    },
    pendingAmount () {
      let pending_amount = 0
      if (this.isEUR) {
        pending_amount = this.amountToBePaidEUR
      } else {
        if (this.invoice.total_amount > 0) {
          pending_amount = this.amountToBePaidEUR
        } else {
          pending_amount = this.totalAmountCurrency
        }
      }

      return pending_amount
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        this.showInvoicesModal = to.meta.showInvoicesModal
        this.modalType = to.meta.modalType
      }
    }
  },
  created () {
    this.invoiceId = (this.isQuotation) ? this.$route.params['quotationId'] : this.$route.params['invoiceId']
    this.previewPDFId = (this.isQuotation) ? this.$route.params['quotationId'] : this.$route.params['invoiceId']
    if (this.invoiceId) {
      this.setupData()
    }
  },
  mounted () {

  },
  methods: {
    ...mapActions({ 
      setInvoiceToCopy: 'invoices/setInvoiceToCopy',
    }),
    ...mapMutations({ 
      setInvoicePDF: 'invoices/SET_INVOICE_PDF',
    }),
    setupData () {
      if (this.invoice) {
        console.log('view this.invoice', this.invoice)
        if (!this.isRecurrent) {
          this.$emit('display-buttons', 'view')
        } else {
          this.$emit('display-buttons', 'view-recurrent')
        }
      } else {
        console.log('Invoice is not set')
      }
      this.loading = false
    },
    closeInvoicesModal () {
      this.showInvoicesModal = false
      this.modalType = null
      this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.invoiceId } })
    },
    closeFinanceModal () {
      this.manualFinanceModalToggle = true
      this.showFinanceModal = false
      window.mixpanel.track('Rabobank: finance modal closed')
    },
    closeAdjustModal () {
      this.showAdjustModal = false
    },
    closeDesignModal () {
      this.showDesign = false
    },
    manualFinanceToggle () {
      this.manualFinanceModalToggle = true
      this.showFinanceModal = true
      window.mixpanel.track('Rabobank: invoice view banner open')
    },
    refreshPDF () {
      console.log('refreshPDF')
      this.showDesign = false
      this.setInvoicePDF(null)
      this.previewPDFId++ // to refresh PDF file after layout update
    },
    goToSubscriptions () {
      window.location.href = window.Urls['subscriptions:manage']()
    }
  }
}
</script>
<style scoped lang="scss">
  .modal-invoice-view-wrap {
    height: 100%;
  }

  .mastermodal-view-wrap {

  }
</style>

<style lang="scss">
  
</style>